import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Autocomplete, TextField } from '@biq/apex-ui';
import { useSmartContractList } from '../../../../hooks';
import {
    AutocompleteChangeDetails,
    AutocompleteChangeReason,
    ListItem,
    Typography,
} from '@mui/material';
import { globalEllipsisTextStyles, SmartContract } from '../../../../lib';
import { styles } from './job-manager-smart-contract-selector-helper';

interface ChainSelectorProps {
    chain: string;
    onChange: (
        event: SyntheticEvent<Element, Event>,
        value: any,
        reason: AutocompleteChangeReason,
        details?: AutocompleteChangeDetails<any> | undefined
    ) => void;
    required?: boolean;
    error?: boolean;
    errorText?: string;
    defaultValue?: SmartContract | null;
    value?: SmartContract | null;
}

export const JobManagerSmartContractSelector = ({
    chain,
    onChange,
    required = false,
    error = false,
    errorText,
    defaultValue,
    value,
    ...other
}: ChainSelectorProps): React.JSX.Element => {
    const [selectedChain, setSelectedChain] = useState<string>('');
    const [smartContractListData, setSmartContractListData] = useState<any[]>(
        []
    );
    const {
        data: smartContractList,
        isLoading,
        refetch: getSmartContracts,
    } = useSmartContractList({
        chain,
        enabled: !!chain,
    });

    useEffect(() => {
        if (!isLoading && smartContractList) {
            smartContractList?.data.sort((a: any, b: any) => {
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
            });
            setSmartContractListData(smartContractList?.data);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoading, selectedChain]);

    useEffect(() => {
        // only fetch smart contracts if there's a selected chain from the parent.
        if (chain && chain !== selectedChain) {
            setSelectedChain(chain);
            getSmartContracts();
        } else if (!chain) {
            setSmartContractListData([]);
        }
    }, [chain]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Autocomplete
            {...other}
            options={smartContractListData}
            defaultValue={defaultValue}
            getOptionLabel={(smartContract: SmartContract): string =>
                smartContract.name
            }
            onChange={onChange}
            value={value}
            sx={styles.autoCompleteStyles}
            renderInput={(params): React.JSX.Element => (
                <TextField
                    {...params}
                    size='medium'
                    required={required}
                    label='Smart Contract'
                    variant='outlined'
                    error={error}
                    errorText={errorText}
                />
            )}
            ListboxProps={{
                sx: styles.smartContractAutocompleteFieldHeight,
            }}
            renderOption={(props, option): React.JSX.Element => (
                <ListItem {...props}>
                    <Typography sx={globalEllipsisTextStyles}>
                        {option?.name}
                    </Typography>
                </ListItem>
            )}
        />
    );
};
